import { ApiError } from '@pn/core/errors';
import { TokenManager } from '@pn/core/services/authentication/tokenManager';
import { isNil } from 'lodash-es';
import { notifyMinimumBuildNumber } from './minimumVersionObserver';
import { getResponseType, isFormData } from './utils';

export type ApiRequestParams = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  url: string;
  urlTemplate?: string; // overrides the auto-generated URL template
  payload?: unknown;
};

type Params = {
  baseUrl: string;
  tokenManager: TokenManager;
  extraHeaders?: HeadersInit;
  params: ApiRequestParams;
  /* Only the PN API serves `X-Minimum-Build-Number` */
  observeMinimumBuildNumber?: boolean;
};

/**
 * **You probably shouldn't use this function directly.**
 *
 * (Use `pnApiClient` or `stackApiClient` instead)
 *
 * {@link ApiError} is thrown in the following cases:
 * - Network errors (CORS, timeout, DNS failure, no internet) → `status = 0`
 * - Unexpected runtime errors (e.g., malformed response, unexpected throw) → `status = -1`
 * - Non-2xx HTTP responses:
 *   - `400-499` → Client-side errors (e.g., validation failures, unauthorized requests)
 *   - `500-599` → Server-side errors (e.g., internal server error, service unavailable)
 *
 * @returns Resolves with the parsed response data.
 */
export async function makeRequest<T>({
  baseUrl,
  tokenManager,
  extraHeaders = {},
  params: { method = 'GET', url, urlTemplate, payload },
  observeMinimumBuildNumber = false,
}: Params): Promise<T> {
  const headers: HeadersInit = new Headers({ ...extraHeaders });

  if (!isFormData(payload)) {
    headers.set('Content-Type', 'application/json');
  }

  const token = await tokenManager.fetchToken();
  if (!isNil(token)) headers.set('Authorization', token);

  const fullUrl = new URL(`${baseUrl}${url}`);

  const requestOptions: RequestInit = { method, headers };

  if (method === 'GET' && payload) {
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined) {
        fullUrl.searchParams.append(key, String(value));
      }
    });
  } else if (['POST', 'PUT', 'DELETE'].includes(method) && payload) {
    requestOptions.body = isFormData(payload)
      ? payload
      : JSON.stringify(payload);
  }

  try {
    const response = await fetch(fullUrl.toString(), requestOptions).catch(
      (error) => {
        throw new ApiError({
          message: `Network error: ${error.message}`,
          code: 0,
          method,
          url,
          urlTemplate,
        });
      }
    );

    const contentType = response.headers.get('Content-Type') ?? '';

    if (observeMinimumBuildNumber) {
      const minBuild = Number(
        response.headers.get('X-Minimum-Build-Number') ?? NaN
      );
      /**
       * A blank header parses to 0, which is indistinguishable from a backend
       * that has no minimum configured, so only positive values are reported.
       */
      if (Number.isFinite(minBuild) && minBuild > 0) {
        notifyMinimumBuildNumber(minBuild);
      }
    }

    if (!response.ok) {
      let errorMessage = '';
      let responseData: any = null;

      if (contentType.includes('application/json')) {
        try {
          // Clone so the original body is still available if JSON parsing fails
          responseData = await response.clone().json();
          errorMessage = responseData.message ?? JSON.stringify(responseData);
        } catch {
          /* JSON parsing failed, will fallback to response.text() */
        }
      }

      if (errorMessage === '') errorMessage = await response.text();

      throw new ApiError({
        message: errorMessage,
        code: response.status,
        method,
        url,
        urlTemplate,
        responseData: responseData ?? errorMessage,
      });
    }

    switch (getResponseType(contentType)) {
      case 'none':
        return undefined as T;
      case 'json':
        return (await response.json()) as T;
      case 'blob':
        return (await response.blob()) as T;
      case 'arrayBuffer':
        return (await response.arrayBuffer()) as T;
      case 'formData':
        return (await response.formData()) as T;
      default:
        return (await response.text()) as T;
    }
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError({
      message: `Unexpected error: ${String(error)}`, // ensures even non-Error types are stringified
      code: -1,
      method,
      url,
      urlTemplate,
    });
  }
}
